/**
 * CAD System Test Suite
 * Comprehensive tests for all CAD functionality
 */

class CADTestSuite {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.results = [];
  }

  /**
   * Run all tests
   */
  async runAllTests() {
    console.log('[CADTestSuite] ========================================');
    console.log('[CADTestSuite] Starting comprehensive CAD test suite');
    console.log('[CADTestSuite] ========================================');

    this.results = [];

    // Core functionality tests
    await this.testDocumentOperations();
    await this.testSketchEngine();
    await this.testPrimitives();
    await this.test3DOperations();
    await this.testPatterns();
    await this.testBooleanOperations();
    await this.testAssembly();
    await this.testDatumFeatures();
    await this.testMeasurements();
    await this.testExport();

    // Module integration tests
    if (typeof HistoryManager !== 'undefined') {
      await this.testUndoRedo();
    }

    if (typeof ProfileDetector !== 'undefined') {
      await this.testProfileDetection();
    }

    // Print results
    this.printResults();

    return this.results;
  }

  /**
   * Test helper - run a test and record result
   */
  async test(name, testFn) {
    console.log(`[CADTestSuite] Running: ${name}...`);

    try {
      await testFn();
      this.results.push({ name, status: 'PASS', error: null });
      console.log(`[CADTestSuite] ✓ PASS: ${name}`);
      return true;
    } catch (error) {
      this.results.push({ name, status: 'FAIL', error: error.message });
      console.error(`[CADTestSuite] ✗ FAIL: ${name}`, error);
      return false;
    }
  }

  // ===== Test Suites =====

  async testDocumentOperations() {
    await this.test('Create new document', async () => {
      const doc = await this.cadViewer.createNewDocument('Test Document');
      if (!doc) throw new Error('Failed to create document');
    });

    await this.test('Document has features array', async () => {
      if (!Array.isArray(this.cadViewer.document.features)) {
        throw new Error('Features array missing');
      }
    });
  }

  async testSketchEngine() {
    await this.test('Sketch engine initialized', async () => {
      if (!this.cadViewer.sketchEngine) {
        throw new Error('Sketch engine not initialized');
      }
    });

    await this.test('Can create sketch entities', async () => {
      const entity = {
        type: 'line',
        id: Utils.generateId(),
        start: { x: 0, y: 0, z: 0 },
        end: { x: 1, y: 1, z: 0 }
      };

      if (!entity.id) throw new Error('Entity creation failed');
    });

    await this.test('All sketch tools available', async () => {
      const requiredTools = ['point', 'line', 'rectangle', 'circle', 'arc', 'spline', 'text', 'ellipse', 'polygon', 'slot'];
      // Just verify the tools exist as buttons
      for (const tool of requiredTools) {
        const btn = document.querySelector(`[data-tool="${tool}"]`);
        if (!btn) throw new Error(`Tool ${tool} not found`);
      }
    });
  }

  async testPrimitives() {
    const primitives = ['box', 'sphere', 'cylinder', 'cone', 'torus', 'wedge'];

    for (const prim of primitives) {
      await this.test(`Create ${prim} primitive`, async () => {
        const btn = document.getElementById(`cadPrim${prim.charAt(0).toUpperCase() + prim.slice(1)}`);
        if (!btn) throw new Error(`${prim} button not found`);
      });
    }
  }

  async test3DOperations() {
    const operations = ['Extrude', 'Pocket', 'Revolve', 'Groove', 'Hole', 'Sweep', 'Loft', 'Fillet', 'Chamfer', 'Shell'];

    for (const op of operations) {
      await this.test(`${op} operation available`, async () => {
        const btn = document.getElementById(`cadOp${op}`);
        if (!btn) throw new Error(`${op} button not found`);
      });
    }
  }

  async testPatterns() {
    const patterns = ['LinearPattern', 'CircularPattern', 'Mirror'];

    for (const pattern of patterns) {
      await this.test(`${pattern} operation available`, async () => {
        const btn = document.getElementById(`cadOp${pattern}`);
        if (!btn) throw new Error(`${pattern} button not found`);
      });
    }
  }

  async testBooleanOperations() {
    await this.test('Boolean CSG library loaded', async () => {
      if (typeof CSG === 'undefined') {
        throw new Error('CSG library not loaded');
      }
    });

    await this.test('Boolean operations available', async () => {
      if (!this.cadViewer.performBooleanAdd) throw new Error('Boolean add not found');
      if (!this.cadViewer.performBooleanCut) throw new Error('Boolean cut not found');
      if (!this.cadViewer.performBooleanIntersect) throw new Error('Boolean intersect not found');
    });
  }

  async testAssembly() {
    await this.test('Assembly system available', async () => {
      if (typeof Assembly === 'undefined') {
        throw new Error('Assembly class not found');
      }
    });

    await this.test('Mate types available', async () => {
      if (typeof Mate === 'undefined') {
        throw new Error('Mate class not found');
      }
    });
  }

  async testDatumFeatures() {
    const datums = ['Plane', 'Axis', 'Point'];

    for (const datum of datums) {
      await this.test(`Datum ${datum} available`, async () => {
        const btn = document.getElementById(`cadDatum${datum}`);
        if (!btn) throw new Error(`Datum ${datum} button not found`);
      });
    }
  }

  async testMeasurements() {
    const measurements = ['Distance', 'Angle', 'Area', 'Volume', 'COM'];

    for (const measure of measurements) {
      await this.test(`Measurement ${measure} available`, async () => {
        const btn = document.getElementById(`cadMeasure${measure}`);
        if (!btn) throw new Error(`Measure ${measure} button not found`);
      });
    }
  }

  async testExport() {
    const formats = ['STL', 'OBJ', 'GLTF'];

    for (const format of formats) {
      await this.test(`${format} export available`, async () => {
        const btn = document.getElementById(`cadExport${format}`);
        if (!btn) throw new Error(`Export ${format} button not found`);
      });
    }
  }

  async testUndoRedo() {
    await this.test('Undo/Redo system loaded', async () => {
      if (!this.cadViewer.historyManager) {
        throw new Error('History manager not found');
      }
    });
  }

  async testProfileDetection() {
    await this.test('Profile detector loaded', async () => {
      if (!this.cadViewer.profileDetector) {
        throw new Error('Profile detector not found');
      }
    });
  }

  /**
   * Print test results summary
   */
  printResults() {
    console.log('[CADTestSuite] ========================================');
    console.log('[CADTestSuite] TEST RESULTS SUMMARY');
    console.log('[CADTestSuite] ========================================');

    const passed = this.results.filter(r => r.status === 'PASS').length;
    const failed = this.results.filter(r => r.status === 'FAIL').length;
    const total = this.results.length;

    console.log(`[CADTestSuite] Total: ${total} tests`);
    console.log(`[CADTestSuite] ✓ Passed: ${passed}`);
    console.log(`[CADTestSuite] ✗ Failed: ${failed}`);
    console.log(`[CADTestSuite] Success Rate: ${((passed / total) * 100).toFixed(1)}%`);

    if (failed > 0) {
      console.log('[CADTestSuite] ========================================');
      console.log('[CADTestSuite] FAILED TESTS:');
      this.results.filter(r => r.status === 'FAIL').forEach(r => {
        console.log(`[CADTestSuite]   ✗ ${r.name}: ${r.error}`);
      });
    }

    console.log('[CADTestSuite] ========================================');

    // Show notification
    if (failed === 0) {
      Utils.notify(`All ${total} tests passed!`, 'success');
    } else {
      Utils.notify(`${passed}/${total} tests passed (${failed} failed)`, 'warning');
    }

    return { passed, failed, total };
  }

  /**
   * Run quick smoke test
   */
  async runSmokeTest() {
    console.log('[CADTestSuite] Running smoke test...');

    const critical = [
      () => this.testDocumentOperations(),
      () => this.testSketchEngine(),
      () => this.testPrimitives()
    ];

    for (const test of critical) {
      await test();
    }

    const passed = this.results.filter(r => r.status === 'PASS').length;
    const total = this.results.length;

    console.log(`[CADTestSuite] Smoke test: ${passed}/${total} passed`);

    return passed === total;
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADTestSuite };
}
